"use client";

import { useState, useEffect, useRef } from "react";
import styles from "./ReadMoreText.module.scss";
import { ReadMoreTextProps } from "./ReadMoreText.types";
import { toKebabCase } from "@utils/stringFormatters";

export default function ReadMoreText({
  text,
  brand,
  model,
  maxLines = 2,
  readMoreText = "read more",
  readLessText = "read less",
  className = "",
  style,
  showReadLess = false,
  onReadMore,
  onReadLess,
  ellipsisClassname,
  readMoreClassname,
}: ReadMoreTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const isMMVPage = sessionStorage.getItem("currentPageType");

  // Single effect for mobile detection and hydration
  useEffect(() => {
    setIsHydrated(true);
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Check if text needs truncation only on mobile
  useEffect(() => {
    if (!isHydrated || !textRef.current || !isMobile) {
      setNeedsTruncation(false);
      return;
    }

    const element = textRef.current;
    const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
    const maxHeight = lineHeight * maxLines;

    // Temporarily remove max-height to measure full height
    const originalMaxHeight = element.style.maxHeight;
    element.style.maxHeight = 'none';
    const fullHeight = element.scrollHeight;
    element.style.maxHeight = originalMaxHeight;

    setNeedsTruncation(fullHeight > maxHeight);
  }, [text, maxLines, isMobile, isHydrated]);

  const handleReadMoreClick = () => {
    setIsExpanded(true);
    onReadMore?.();
  };

  const handleReadLessClick = () => {
    setIsExpanded(false);
    onReadLess?.();
  };

  // Calculate max height for CSS custom properties
  const maxHeight = `${maxLines * 1.5}em`;
  const mobileMaxHeight = `${maxLines * 1.4}em`;

  // If not mobile or doesn't need truncation, show full text
  if (!isMobile || !needsTruncation) {
    return (
      <div className={`${styles.readMoreWrapper} ${className}`} style={style}>
        {
          isMMVPage === "MMV page" ?
            <p className={`${styles.text} ${styles.expanded}`} ref={textRef}>
              <a className={styles.textLink} href={`/cars/${toKebabCase(brand)}-${toKebabCase(model)}`}>{brand} {model}</a> {text}
            </p>
            :
            <p className={`${styles.text} ${styles.expanded}`} ref={textRef}>
              {brand} {model} {text}
            </p>
        }
      </div>
    );
  }

  // Mobile with truncation needed
  return (
    <div className={`${styles.readMoreWrapper} ${className}`} style={style}>
      {isExpanded ? (
        isMMVPage === "MMV page" ?
          <p className={`${styles.text} ${styles.expanded}`} ref={textRef}>
            <a className={styles.textLink} href={`/cars/${toKebabCase(brand)}-${toKebabCase(model)}`}>{brand} {model}</a> {text}
            {showReadLess && (
              <button
                className={styles.readLessButton}
                onClick={handleReadLessClick}
                type="button"
              >
                {readLessText}
              </button>
            )}
          </p>
          :
          <p className={`${styles.text} ${styles.expanded}`} ref={textRef}>
            {brand} {model} {text} {showReadLess && (
              <button
                className={styles.readLessButton}
                onClick={handleReadLessClick}
                type="button"
              >
                {readLessText}
              </button>
            )}
          </p>
      ) : (
        isMMVPage === "MMV page" ?
          <p
            ref={textRef}
            className={`${styles.text} ${styles.collapsed}`}
            style={{
              '--max-height': maxHeight,
              '--mobile-max-height': mobileMaxHeight,
            } as React.CSSProperties}
          >
            <a className={styles.textLink} href={`/cars/${toKebabCase(brand)}-${toKebabCase(model)}`}>{brand} {model}</a> {text}
            <span className={`${styles.ellipsis} ${ellipsisClassname || ''}`}>...</span>
            <button
              className={`${styles.readMoreButton} ${readMoreClassname || ''}`}
              onClick={handleReadMoreClick}
              type="button"
            >
              {readMoreText}
            </button>
          </p>
          :
          <p
            ref={textRef}
            className={`${styles.text} ${styles.collapsed}`}
            style={{
              '--max-height': maxHeight,
              '--mobile-max-height': mobileMaxHeight,
            } as React.CSSProperties}
          >
            {brand} {model} {text} <span className={`${styles.ellipsis} ${ellipsisClassname || ''}`}>...</span>
            <button
              className={`${styles.readMoreButton} ${readMoreClassname || ''}`}
              onClick={handleReadMoreClick}
              type="button"
            >
              {readMoreText}
            </button>
          </p>
      )}
    </div>
  );
}
